/**
 * Phone Button Component
 *
 * Responsive button that adapts based on device:
 * - Desktop (≥1024px): Links to /contact page
 * - Mobile (<1024px): tel: link to call directly
 *
 * Uses CSS-based responsive behavior (no JS detection needed).
 * Aligns with Tailwind's lg breakpoint (1024px).
 *
 * Following CLAUDE.md:
 * - Uses Tailwind CSS utility classes for styling
 * - Uses next/link for internal navigation
 * - Client component for interactive state
 */

"use client";

import type { VariantProps } from "class-variance-authority";
import { Phone } from "lucide-react";
import Link from "next/link";
import { Button, type buttonVariants } from "@/components/ui/button";
import { TrackedAnchor } from "@/components/ui/tracked-link/tracked-link";
import { cn } from "@/lib/utils";

export interface PhoneButtonProps {
	phoneNumber: string;
	displayText?: string;
	variant?: VariantProps<typeof buttonVariants>["variant"];
	size?: VariantProps<typeof buttonVariants>["size"];
	className?: string;
	showIcon?: boolean;
}

export function PhoneButton({
	phoneNumber,
	displayText,
	variant = "default",
	size = "default",
	className,
	showIcon = true,
}: PhoneButtonProps) {
	// Format phone number for tel: link (remove spaces, add +61)
	const telLink = `tel:+61${phoneNumber.replace(/\s/g, "")}`;
	const display = displayText || phoneNumber;

	return (
		<>
			{/* Mobile: tel: link (< lg breakpoint) */}
			<Button
				asChild
				variant={variant}
				size={size}
				className={cn("lg:hidden", className)}
			>
				<TrackedAnchor href={telLink} event="telephone_link_clicks">
					{showIcon && <Phone className="mr-2 h-4 w-4" />}
					{display}
				</TrackedAnchor>
			</Button>

			{/* Desktop: Link to /contact (≥ lg breakpoint) */}
			<Button
				asChild
				variant={variant}
				size={size}
				className={cn("hidden lg:inline-flex", className)}
			>
				<Link href="/contact">
					{showIcon && <Phone className="mr-2 h-4 w-4" />}
					{display}
				</Link>
			</Button>
		</>
	);
}
